/**
 * The automation tab's controls: the automation's own switch, "stop
 * everything", and the way to the settings behind both.
 *
 * The switch writes straight through (`saveAiOutboundSettings({ autopilot })`),
 * like the other autopilots in this CRM: a switch with a save step is not a
 * switch. It sends only `autopilot`, so a page left open can never re-send a
 * stale `enabled` and switch AI calling back on after a stop.
 *
 * "Stop everything" asks first, because it does three things at once: AI calling
 * and the automation go off, a running tick is waited out, and every AI call
 * still on the line is hung up. What it did comes back as one note. Turning
 * calling back on is deliberately not offered here: that decision belongs to
 * the settings page, next to the readiness it depends on.
 *
 * Managers only, as the server checks (`manage:crm.calls`).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, OctagonX, Settings2 } from "lucide-react";
import { ConfirmDialog } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { invalidateAiOutboundStatus, saveAiOutboundSettings } from "../../../../lib/aiOutbound";
import { stopAiOutbound, stopError, type StopError, type StopReply } from "../../../../lib/aiAutomation";
import { Btn } from "../../../ui";
import { Note, ToggleRow } from "../../settings/panel/fields";
import { stopLines } from "./automationView";

/** Settings -> Phone -> AI calls out, where the window, gap, caps and readiness are set. */
export const AUTOMATION_SETTINGS_PATH = "/settings/phone/ai-outbound";

export function AutomationSettingsLink() {
  const t = useT();
  return (
    <Link to={AUTOMATION_SETTINGS_PATH} className="crm-btn">
      <Settings2 className="size-3.5" />
      {t("aiCalls.automation.controls.settings")}
    </Link>
  );
}

export function AutopilotToggle({
  autopilot,
  enabled,
  canManage,
  onChanged,
}: {
  autopilot: boolean;
  /** AI calling at all; the automation does nothing while it is off. */
  enabled: boolean;
  canManage: boolean;
  /** Re-reads the status; awaited, so the switch does not flick back before the new value arrives. */
  onChanged: () => Promise<void>;
}) {
  const t = useT();
  const [saving, setSaving] = useState(false);
  // The switch shows the value being written until the status re-read lands.
  const [pending, setPending] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);

  async function change(next: boolean) {
    setSaving(true);
    setPending(next);
    setFailed(false);
    try {
      await saveAiOutboundSettings({ autopilot: next });
      invalidateAiOutboundStatus();
    } catch {
      setFailed(true);
    }
    await onChanged();
    setSaving(false);
    setPending(null);
  }

  const checked = pending ?? autopilot;
  return (
    <div className="space-y-2">
      <ToggleRow
        label={t("aiCalls.automation.controls.autopilot")}
        hint={t("aiCalls.automation.controls.autopilotHint")}
        checked={checked}
        disabled={!canManage || saving}
        onChange={(next) => void change(next)}
      />
      {checked && !enabled ? <Note tone="warn">{t("aiCalls.automation.controls.needsEnable")}</Note> : null}
      {failed ? (
        <Note tone="bad" live>
          {t("aiCalls.automation.controls.saveError")}
        </Note>
      ) : null}
    </div>
  );
}

/** What "stop everything" came back with: the counts, or why it did not stop. */
export type StopResult = { reply: StopReply } | { error: StopError };

export function StopAllButton({
  onStopped,
  quiet,
  onAsking,
}: {
  onStopped: (result: StopResult) => void;
  /** A small plain button, for a place where another red button is the main action (the live dock). */
  quiet?: boolean;
  /** The confirmation opened or closed: a place that can leave meanwhile (the live dock) stays while it asks. */
  onAsking?: (asking: boolean) => void;
}) {
  const t = useT();
  const [confirm, setConfirmState] = useState(false);
  const [stopping, setStopping] = useState(false);
  const setConfirm = (asking: boolean) => {
    setConfirmState(asking);
    onAsking?.(asking);
  };

  async function stop() {
    setStopping(true);
    let result: StopResult;
    try {
      result = { reply: await stopAiOutbound() };
      invalidateAiOutboundStatus();
    } catch (err) {
      result = { error: stopError(err) };
    }
    setConfirm(false);
    onStopped(result);
    setStopping(false);
  }

  return (
    <>
      <Btn tone={quiet ? "plain" : "danger"} small={quiet} onClick={() => setConfirm(true)} disabled={stopping}>
        {stopping ? <Loader2 className="size-3.5 animate-spin" /> : <OctagonX className="size-3.5" />}
        {t("aiCalls.automation.controls.stop")}
      </Btn>
      <ConfirmDialog
        open={confirm}
        title={t("aiCalls.automation.controls.stopTitle")}
        description={t("aiCalls.automation.controls.stopConfirm")}
        confirmLabel={t("aiCalls.automation.controls.stop")}
        cancelLabel={t("common.cancel")}
        variant="destructive"
        loading={stopping}
        onConfirm={() => void stop()}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
}

/** What the stop did, shown under the tab row until it is closed. */
export function StopNote({ result, onClose }: { result: StopResult; onClose: () => void }) {
  const t = useT();
  const text =
    "reply" in result
      ? [t("aiCalls.automation.controls.stopped"), ...stopLines(result.reply).map((line) => t(line.key, { n: line.n }))].join(" ")
      : t(`aiCalls.automation.controls.stopError.${result.error}`);
  return (
    <Note tone={"reply" in result ? "ok" : "bad"} live>
      <span className="flex flex-wrap items-center justify-between gap-2">
        <span>{text}</span>
        <Btn small onClick={onClose}>
          {t("common.close")}
        </Btn>
      </span>
    </Note>
  );
}
